import { supabase } from '../lib/supabase_client';
import { sanitizeUploadFilename } from './sanitizeUploadFilename';

export type UploadFilingPdfV5Result = {
  success: true;
  filing_id: string;
  credits_remaining?: number;
  balance_remaining?: number;
  billing_warning?: string;
  workflow_execution_arn?: string | null;
};

type PrepareResponse = {
  upload_url?: string;
  s3_key?: string;
  filing_id?: string;
  filename?: string;
  content_type?: string;
  error?: string;
  free_credit?: number;
  currency_balance?: number;
  required?: number;
};

type CompleteResponse = {
  success?: boolean;
  filing_id?: string;
  credits_remaining?: number;
  balance_remaining?: number;
  billing_warning?: string;
  workflow_execution_arn?: string | null;
  error?: string;
  free_credit?: number;
  currency_balance?: number;
  required?: number;
};

/**
 * v5 = v3 flow (prepare → PUT → complete) with one leak fix:
 * after the PDF is on S3, a lost complete reply is not shown as fail.
 * No byte-size check (that was v4). No S3 webhook.
 */
export async function uploadFilingPdfV5(opts: {
  accessToken: string;
  file: File;
  orgId: string;
  filingTypeId?: string | null;
  groupId?: string | null;
  overlay?: string | boolean | null;
}): Promise<UploadFilingPdfV5Result> {
  const { accessToken, file, orgId, filingTypeId, groupId, overlay } = opts;
  const sanitizedFilename = sanitizeUploadFilename(file.name);
  const authHeaders = { Authorization: `Bearer ${accessToken}` };

  const prepareBody: Record<string, unknown> = {
    action: 'prepare',
    org_id: orgId,
    filename: sanitizedFilename,
  };
  if (filingTypeId) prepareBody.filing_type_id = filingTypeId;
  if (groupId) prepareBody.group_id = groupId;
  if (overlay != null) prepareBody.overlay = String(overlay);

  const { data: prepareData, error: prepareError } = await supabase.functions.invoke(
    'upload-filing-pdf-v5',
    { body: prepareBody, headers: authHeaders }
  );

  if (prepareError) {
    throw new Error(`Upload prepare failed: ${prepareError.message}`);
  }

  const prepare = prepareData as PrepareResponse | null;
  if (prepare?.error === 'INSUFFICIENT_BALANCE') {
    const err = new Error('INSUFFICIENT_BALANCE') as Error & {
      code: 'INSUFFICIENT_BALANCE';
      free_credit?: number;
      currency_balance?: number;
      required?: number;
    };
    err.code = 'INSUFFICIENT_BALANCE';
    err.free_credit = prepare.free_credit;
    err.currency_balance = prepare.currency_balance;
    err.required = prepare.required;
    throw err;
  }
  if (prepare?.error) throw new Error(prepare.error);
  if (!prepare?.upload_url || !prepare.filing_id || !prepare.s3_key) {
    throw new Error('Upload prepare did not return upload_url / filing_id / s3_key');
  }

  const { upload_url, filing_id, s3_key, content_type } = prepare;
  const filename = prepare.filename || sanitizedFilename;

  try {
    const putRes = await fetch(upload_url, {
      method: 'PUT',
      headers: {
        'Content-Type': content_type || 'application/pdf',
      },
      body: file,
    });

    if (!putRes.ok) {
      const text = await putRes.text().catch(() => '');
      throw new Error(
        `S3 upload failed (${putRes.status}): ${text.slice(0, 200) || putRes.statusText}`
      );
    }
  } catch (putErr: any) {
    try {
      await supabase.functions.invoke('upload-filing-pdf-v5', {
        body: { action: 'cancel', filing_id },
        headers: authHeaders,
      });
    } catch {
      /* ignore cancel errors */
    }
    const msg = String(putErr?.message || putErr);
    if (/Failed to fetch|NetworkError|CORS/i.test(msg)) {
      throw new Error(
        'Direct S3 upload blocked (CORS/network). Ensure the S3 bucket CORS allows PUT from this origin.'
      );
    }
    throw putErr instanceof Error ? putErr : new Error(msg);
  }

  const complete = await completeOnce({
    authHeaders,
    filing_id,
    org_id: orgId,
    s3_key,
    filename,
  });

  return {
    success: true,
    filing_id,
    credits_remaining: complete?.credits_remaining,
    balance_remaining: complete?.balance_remaining,
    billing_warning: complete?.billing_warning,
    workflow_execution_arn: complete?.workflow_execution_arn ?? null,
  };
}

async function completeOnce(opts: {
  authHeaders: { Authorization: string };
  filing_id: string;
  org_id: string;
  s3_key: string;
  filename: string;
}): Promise<CompleteResponse | null> {
  try {
    const { data, error } = await supabase.functions.invoke('upload-filing-pdf-v5', {
      body: {
        action: 'complete',
        filing_id: opts.filing_id,
        org_id: opts.org_id,
        s3_key: opts.s3_key,
        filename: opts.filename,
      },
      headers: opts.authHeaders,
    });

    if (error) return null;

    const complete = data as CompleteResponse | null;
    if (complete?.error === 'INSUFFICIENT_BALANCE') {
      const err = new Error('INSUFFICIENT_BALANCE') as Error & { code: 'INSUFFICIENT_BALANCE' };
      err.code = 'INSUFFICIENT_BALANCE';
      throw err;
    }
    if (complete?.success === true) return complete;
  } catch (err: any) {
    if (err?.code === 'INSUFFICIENT_BALANCE') throw err;
  }

  return null;
}
